import * as React from 'react';
import { useState } from 'react';
import styled, { css } from 'styled-components';

import { Timestamp, TextOverflowEllipsis, IconButton } from 'components/common';
import type { Evidence } from 'security-app/components/Investigations/Details/TimelineSegment/types';
import { evidenceTypeLabel } from 'security-app/components/Investigations/Details/TimelineSegment/Utils';
import { Badge, ButtonToolbar, Button } from 'components/bootstrap';

export const evidenceCardId = (evidenceId: string) => `evidence-card-${evidenceId}`;

const Container = styled.div`
  display: flex;
  width: 100%;
  gap: 10px;
  flex-direction: column;
`;

const TopRow = styled.div`
  display: flex;
  width: 100%;
  gap: 4px;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;

const LeftHeaderCol = styled.div`
  display: flex;
  gap: 5px;
  align-items: center;
  flex-wrap: wrap;
`;

const Header = styled.div(
  ({ theme }) => css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: ${theme.fonts.size.small};
  `,
);

const StyledTimestamp = styled(Timestamp)(
  ({ theme }) => css`
    color: ${theme.colors.text.secondary};
  `,
);

const Title = styled.h5`
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  word-wrap: break-word;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const Subtitle = styled(TextOverflowEllipsis)(
  ({ theme }) => css`
    color: ${theme.colors.text.secondary};
    font-size: ${theme.fonts.size.small};
    margin-top: 4px;
  `,
);

const StyledButtonToolbar = styled(ButtonToolbar)`
  flex-wrap: wrap;
`;

const EvidenceCardFoundation = ({
  children = undefined,
  actions,
  evidence,
  hasSimilarFieldsFilter,
  onResetShowSimilar,
  onShowSimilar,
  subtitle = undefined,
  title,
  isHighlighted = false,
  canSeeEventDefinition = true,
}: React.PropsWithChildren<{
  actions: React.ReactNode;
  evidence: Evidence;
  hasSimilarFieldsFilter: boolean;
  onResetShowSimilar: () => void;
  onShowSimilar: (targetEvidence: Evidence) => Promise<void>;
  subtitle?: string;
  title: React.ReactNode;
  isHighlighted?: boolean;
  canSeeEventDefinition?: boolean;
}>) => {
  const [showMore, setShowMore] = useState(hasSimilarFieldsFilter);
  const [showingSimilar, setShowingSimilar] = useState(false);
  const typeTitle = evidenceTypeLabel(evidence.type);
  const toggleFields = () => setShowMore((cur) => !cur);

  const showSimilar = async () => {
    setShowingSimilar(true);

    onShowSimilar(evidence).finally(() => {
      setShowingSimilar(false);
    });
  };

  return (
    <Container>
      <TopRow>
        <Header>
          <LeftHeaderCol>
            {isHighlighted ? <Badge bsStyle="primary">Selected {typeTitle}</Badge> : <b>{typeTitle}</b>}
            <StyledTimestamp dateTime={evidence.timestamp} />
          </LeftHeaderCol>
          {canSeeEventDefinition && (
            <IconButton
              name={showMore ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
              onClick={toggleFields}
              title={`Show ${evidenceTypeLabel(evidence.type, true)} details`}
            />
          )}
        </Header>
        <Title>{title}</Title>
        {subtitle && <Subtitle>{subtitle}</Subtitle>}
      </TopRow>
      {showMore && (
        <div>
          <StyledButtonToolbar>
            {actions}
            {hasSimilarFieldsFilter ? (
              <Button onClick={onResetShowSimilar} bsSize="small">
                Reset
              </Button>
            ) : (
              <Button onClick={showSimilar} bsSize="small" disabled={showingSimilar}>
                {showingSimilar ? 'Showing...' : 'Show similar'}
              </Button>
            )}
          </StyledButtonToolbar>
          {children}
        </div>
      )}
    </Container>
  );
};

export default EvidenceCardFoundation;
